import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import Modal from "./Modal";
import { formatDate } from "../utils/format";

const MAX_REASON = 200;

// `more` is for approved users who used up their AI credits.
export function RequestAccessModal({ show, onClose, more = false }) {
  const { requestAccess, toast } = useApp();
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    const ok = await requestAccess(reason.trim());
    setSending(false);
    if (!ok) return;
    toast(
      more
        ? "Request sent. Your credits will be topped up once it's approved."
        : "Request sent. You'll get access as soon as it's approved.",
    );
    setReason("");
    onClose();
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={more ? "Ask for more AI credits" : "Request access to AI features"}
      footer={
        <>
          <button className="btn btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={send}
            disabled={sending || !reason.trim()}
          >
            {sending && (
              <span className="spinner-border spinner-border-sm me-2" />
            )}
            {more ? "Request more credits" : "Request AI access"}
          </button>
        </>
      }
    >
      <p className="small text-muted">
        {more
          ? "Tailoring a resume, writing a cover letter or answers, and each step of a Platypus run use one credit each. An admin will look at your request."
          : "Resume tailoring, cover letters, AI answers and Platypus cost money to run, so they're available by request. Everything else is free."}
      </p>
      <label className="form-label small fw-medium" htmlFor="access-reason">
        {more ? "What do you still need them for?" : "How would you use them?"}
      </label>
      <textarea
        id="access-reason"
        className="form-control"
        rows={3}
        maxLength={MAX_REASON}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Applying to new grad software roles this fall"
        autoFocus
      />
      <div className="form-text text-end">
        {reason.length}/{MAX_REASON}
      </div>
    </Modal>
  );
}

// "87 of 100 AI credits left". Renders nothing for admins, who have no limits, or without access.
export function UsageLeft({ service = "ai", className = "small text-muted" }) {
  const { integrations } = useApp();
  const limits = integrations.limits?.ai;
  if (
    !limits ||
    !integrations.access?.allowed ||
    !integrations.configured?.[service] ||
    limits.left === 0
  )
    return null;
  return (
    <div
      className={className}
    >{`${limits.left} of ${limits.total} AI credits left`}</div>
  );
}

// The main call to action for anyone who can't use an AI feature yet: a big, bright
// "Request AI access" button (or a clear waiting state once they've asked).
function AccessCta({
  icon,
  title,
  text,
  action,
  onAction,
  pending = false,
  className = "",
}) {
  return (
    <div
      className={`access-cta ${pending ? "access-cta-pending" : ""} ${className}`}
      role="alert"
    >
      <div className="access-cta-icon" aria-hidden="true">
        <i className={`bi ${icon}`} />
      </div>
      <div className="access-cta-copy">
        <div className="access-cta-title">{title}</div>
        <div className="access-cta-text">{text}</div>
      </div>
      {action && (
        <button
          type="button"
          className="btn btn-access btn-lg"
          onClick={onAction}
        >
          <i className="bi bi-stars me-2" aria-hidden="true" />
          {action}
        </button>
      )}
    </div>
  );
}

// What to show in place of a paid feature the user can't use yet. Renders nothing when they can.
function AccessNotice({ feature, service = "ai", message, className = "" }) {
  const { integrations } = useApp();
  const [asking, setAsking] = useState(false);
  const { access = {}, configured = {}, limits } = integrations;
  if (integrations[service]) return null;

  if (!configured[service]) {
    return (
      <div className={className || "alert alert-info py-2 small mb-0"}>
        {feature} isn't set up on this server yet.
      </div>
    );
  }
  const note = access.note ? ` ${access.note}` : "";
  const requestedOn = access.requestedAt
    ? ` on ${formatDate(access.requestedAt)}`
    : "";

  if (access.allowed && service === "ai" && limits?.ai?.left === 0) {
    return (
      <>
        {access.wantsMore ? (
          <AccessCta
            pending
            icon="bi-hourglass-split"
            title="More AI credits requested"
            text={`You asked for more credits${requestedOn}. ${feature} will turn back on as soon as an admin approves it.`}
            className={className}
          />
        ) : (
          <AccessCta
            icon="bi-coin"
            title="You're out of AI credits"
            text={`You've used all ${limits.ai.total} of your AI credits, and ${feature} needs them.${note} Request a free top-up and an admin will review it.`}
            action="Request more AI credits"
            onAction={() => setAsking(true)}
            className={className}
          />
        )}
        <RequestAccessModal
          show={asking}
          onClose={() => setAsking(false)}
          more
        />
      </>
    );
  }
  if (access.allowed) {
    return (
      <div className={className || "alert alert-info py-2 small mb-0"}>
        {feature} isn't available right now.
      </div>
    );
  }

  let cta;
  if (access.status === "pending") {
    cta = {
      pending: true,
      icon: "bi-hourglass-split",
      title: "AI access requested - waiting for approval",
      text: `You requested access${requestedOn}. ${feature} will turn on as soon as an admin approves it, and we'll email you when that happens.`,
    };
  } else if (access.status === "rejected") {
    cta = {
      icon: "bi-lock",
      title: "Your AI access request wasn't approved",
      text: `You're welcome to ask again.${note} AI features like ${feature} are free once approved - an admin reviews every request.`,
      action: "Request AI access again",
    };
  } else if (access.status === "revoked") {
    cta = {
      icon: "bi-lock",
      title: "Your AI access has ended",
      text: `You can request it again below.${note} AI features like ${feature} are free once approved - an admin reviews every request.`,
      action: "Request AI access again",
    };
  } else {
    cta = {
      icon: "bi-lock",
      title: "You don't have AI access yet",
      text:
        message ||
        `${feature} uses AI, which costs money to run, so it's available by request. Request access below - it's free, an admin reviews it, and everything else in Plateo stays free either way.`,
      action: "Request AI access",
    };
  }

  return (
    <>
      <AccessCta
        {...cta}
        onAction={() => setAsking(true)}
        className={className}
      />
      <RequestAccessModal show={asking} onClose={() => setAsking(false)} />
    </>
  );
}

export default AccessNotice;
